import type { CSSProperties, HTMLAttributes } from "react";
import { Kicker } from "../../ui/v2/Kicker";
import { scaledFont, scaledPx } from "@/styles/scaledFont";

export interface RhythmRow {
  /** MORNING | AFTERNOON | EVENING, in the caller's locale — `t('…')`. */
  slot: string;
  /** One imperative sentence. */
  line: string;
}

export interface RhythmListProps extends HTMLAttributes<HTMLDivElement> {
  /** The block's kicker — `t('…')`, never a literal. */
  title: string;
  rows: ReadonlyArray<RhythmRow>;
}

const BLOCK: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
};

const ROW: CSSProperties = {
  display: "flex",
  gap: 14,
  alignItems: "baseline",
};

/* `400 11px/19px` mono: the mono kicker set on the body's 19px leading so the
 * slot sits on the sentence's first line. Neither `--t-label` (16px leading)
 * nor `--t-data` (13px) is that. The 78px column is the handoff's — it is what
 * "AFTERNOON" needs at this size. */
const SLOT: CSSProperties = {
  font: scaledFont("400 11px/19px var(--font-mono)"),
  letterSpacing: ".06em",
  color: "var(--d-fg-3)",
  width: scaledPx(78),
  flex: "none",
};

const LINE: CSSProperties = {
  font: scaledFont("500 13px/19px var(--font-body)"),
  color: "var(--d-fg-2)",
  textWrap: "pretty",
};

/**
 * "How the day moves" — the three slots of the day, each one instruction.
 * The lines are rhetorical, not aspect reports (the roadmap's "one
 * computation, rhetorical split").
 *
 * Ported from `RhythmList` in
 * `docs/design/handoff/design/components/today/RhythmList.jsx`.
 */
export function RhythmList({
  title,
  rows,
  className,
  style,
  ...rest
}: RhythmListProps): JSX.Element {
  return (
    <div className={className} style={{ ...BLOCK, ...style }} {...rest}>
      <Kicker>{title}</Kicker>
      {rows.map((row) => (
        <div key={row.slot} data-rhythm-row style={ROW}>
          <span style={SLOT}>{row.slot}</span>
          <span style={LINE}>{row.line}</span>
        </div>
      ))}
    </div>
  );
}

export default RhythmList;
